import UserImage from '@components/UserImage';
import { useUser } from '@hooks';
import Buttons from '@components/Buttons';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { ChatsCircle } from '@phosphor-icons/react';
import CustomLink from '@components/CustomLink';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as ApiModels from '@typings/api-models';
import s from './UserWorkflowDetailCustomerView.module.scss';

type Props = {
  owner: {
    id: string;
    name: string | null;
    image: {
      url: string;
    } | null;
    primaryColour: {
      hex: string;
    } | null;
  };
  isCreatorView: boolean;
  userWorkflow: Pick<ApiModels.UserWorkflow, 'id' | 'conversationSid' | 'status'> & {
    person: Pick<ApiModels.Person, 'id'>;
  };
};

const OwnerInfo: React.FC<Props> = ({ owner, isCreatorView, userWorkflow }) => {
  const [user] = useUser();
  const router = useRouter();
  const [isRoutingToChat, setIsRoutingToChat] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const { getData: updateUserWorkflow } = useNoodleApi(tsClient.workflows.updateUserWorkflow);

  const archive = async (): Promise<void> => {
    setIsArchiving(true);
    await updateUserWorkflow({ updates: {status: 'archived'}, userWorkflowId: userWorkflow.id });
    router.push(getUrl(IDENTIFIERS.DASHBOARD_WORKFLOWS));
  };

  const handleRouteToChat = (): void => {
    setIsRoutingToChat(true);
    if (userWorkflow?.conversationSid) {
      router.push(getUrl(IDENTIFIERS.DASHBOARD_CONVERSATION, { conversationSid: userWorkflow.conversationSid }));
    }
  };

  if (!user || owner.id === user.id || !isCreatorView) {
    return null;
  }
  return (
    <div className={s.userInfo}>
      <UserImage url={owner.image?.url} name={owner.name} color={owner.primaryColour?.hex} size={36} />
      <div className={s.userInfoText}>
        <h2 className='body-sm-bold'>{owner.name}</h2>
        <CustomLink to={getUrl(IDENTIFIERS.DASHBOARD_MEMBER, { id: owner.id })} className='caption-bold'>Open Profile</CustomLink>
      </div>
      <div className={s.rightContainer}>
        {isCreatorView && userWorkflow.status === 'data-collection' && (
          <Buttons className={s.archiveButton} isWrapper isFetching={isArchiving} onClick={archive}>
            <p>
              Archive
            </p>
          </Buttons>
        )}
        {userWorkflow.status === 'archived' && (
          <p className={s.archivedText}>Archived</p>
        )}
        <Buttons
          disabled={!userWorkflow.conversationSid}
          isShimmering={isRoutingToChat}
          icon={<ChatsCircle weight="fill" />}
          onClick={handleRouteToChat}
          ariaLabel='Open chat'
          className={s.userInfoChatButton}
        />
      </div>
    </div>
  );
};

export default OwnerInfo;
